import React, { useState, useCallback, useMemo, useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import GridSummary from "aldoo-ra/GridSummary"
import Tabs from "aldoo-ra/Tabs"
import DataTable from "aldoo-ra/DataTable"
import Checkbox from "aldoo-ra/Checkbox"
import Accordion from "aldoo-ra/Accordion"
import Localization from "aldoo-ra/Localization"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { RemoveUserFromLicense, DeactivateDevice, GetLicenseUsers, GetLicenseDevices, RemoveUsersFromLicense, DeactivateDevices } from "aldoo-ra/Admin/ProductLicensing/api"
import { DocumentDuplicateIcon, UserMinusIcon, UsersIcon, DevicePhoneMobileIcon, TrashIcon, UserPlusIcon, InformationCircleIcon, CheckCircleIcon, ExclamationTriangleIcon, ClipboardDocumentListIcon, BookOpenIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { getPlatformIcon } from "aldoo-ra/Admin/ProductLicensing/PlatformIcons"
import { useSearchParamValue, updateQueryParams } from "aldoo-ra/SearchParams"
import SeatManagement from "./SeatManagement"
import InviteUsers from "./InviteUsers"

const LicenseInfo = ({ license, userProfile, onLicenseUpdate }) => {
  // Get current tab from URL, default to "details"
  const urlTab = useSearchParamValue('tab', 'details')
  const [activeTab, setActiveTab] = useState(urlTab)
  const [refreshKey, setRefreshKey] = useState(0)

  // Live seat changes from /rt/account (app login / Refresh License /
  // deactivations elsewhere) — refetch the tables without a reload.
  useEffect(() => {
    const token = PubSub.subscribe("license.devices_changed", () =>
      setRefreshKey((prev) => prev + 1)
    )
    return () => PubSub.unsubscribe(token)
  }, [])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectedDevices, setSelectedDevices] = useState([])
  const [userCount, setUserCount] = useState(0)
  const [deviceCount, setDeviceCount] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const { t } = Localization()
  const texts = TextBundle({ id: "license_info" })

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Horizontal scroll state for the tabs bar (mobile) — same pattern as
  // the Account panel navigation: scrollable with chevrons left/right.
  const tabsScrollRef = useRef(null)
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false)
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false)
  const checkTabsScroll = useCallback(() => {
    const el = tabsScrollRef.current
    if (!el) return
    setCanScrollTabsLeft(el.scrollLeft > 0)
    setCanScrollTabsRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])
  useEffect(() => {
    checkTabsScroll()
    const el = tabsScrollRef.current
    if (!el) return
    el.addEventListener('scroll', checkTabsScroll)
    window.addEventListener('resize', checkTabsScroll)
    return () => {
      el.removeEventListener('scroll', checkTabsScroll)
      window.removeEventListener('resize', checkTabsScroll)
    }
  }, [checkTabsScroll])

  // Sync local state with URL parameter and clear invalid tabs
  useEffect(() => {
    const validTabs = ['details', 'users', 'devices', 'guide']
    if (urlTab && validTabs.includes(urlTab)) {
      setActiveTab(urlTab)
    } else if (urlTab && !validTabs.includes(urlTab)) {
      // Clear invalid tab parameter and select first tab
      setActiveTab('details')
      updateQueryParams({ tab: null })
    }
  }, [urlTab])

  // Handle tab change and update URL
  const handleTabChange = (newTab) => {
    setActiveTab(newTab)
    updateQueryParams({ tab: newTab })
  }

  // Use requestFields to pass licenseId to the API methods
  // This avoids wrapping the API methods and prevents infinite re-renders
  const requestFields = useMemo(() => ({
    licenseId: license._id
  }), [license._id])

  // Create stable onDataLoaded callbacks
  // Note: Don't call onLicenseUpdate on every data load as it causes infinite loops
  // Only call onLicenseUpdate when actual changes happen (in action handlers)
  const handleUsersDataLoaded = useCallback((data) => {
    // Update user count for any future use
    setUserCount(data?.length || 0)
    // Return the data so DataTable can display it
    return data
  }, [])

  const handleDevicesDataLoaded = useCallback((data) => {
    // Update device count for header display
    setDeviceCount(data?.length || 0)
    // Return the data so DataTable can display it
    return data
  }, [license._id, requestFields])

  // Handle copying license code to clipboard
  const copyLicenseCode = async () => {
    try {
      await navigator.clipboard.writeText(license.code)
      showNotification({
        message: texts?.license_code_copied,
        className: "bg-success text-white"
      })
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = license.code
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)

      showNotification({
        message: texts?.license_code_copied,
        className: "bg-success text-white"
      })
    }
  }

  // Handle device deactivation
  const handleDeactivateDevice = (deviceId) => {
    const device = license.devices?.find(d => d.deviceId === deviceId || d.deviceID === deviceId)
    const deviceName = device?.name || device?.deviceName || deviceId || texts?.unknown_device

    showPromptDialog({
      title: texts?.deactivate_device_title,
      message: t(texts?.deactivate_device_message, { deviceName }),
      confirmText: texts?.confirm_deactivate,
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          const response = await DeactivateDevice({
            licenseId: license._id,
            deviceId: deviceId
          })

          if (response.result) {
            showNotification({
              message: t(texts?.deactivation_success, { deviceName }),
              className: "bg-success text-white",
            })
            setRefreshKey(prev => prev + 1)
            if (onLicenseUpdate) {
              onLicenseUpdate()
            }
          } else {
            showNotification({
              message: response.message || texts?.deactivation_failed,
              className: "bg-error text-white",
            })
          }
        } catch (error) {
          console.error('Device deactivation error:', error)
          showNotification({
            message: texts?.deactivation_error,
            className: "bg-error text-white",
          })
        }
      },
    })
  }

  // Handle removing user from license
  const handleRemoveUser = (user, isOwner = false) => {
    // Don't allow removing users if current user is not the license owner
    if (!license.isOwner) {
      return
    }

    const userType = isOwner ? texts?.role_primary_owner : texts?.role_secondary_user
    const userTypeLower = isOwner ? texts?.role_primary_owner_lower : texts?.role_secondary_user_lower
    const userName = user?.name || user?.email || texts?.unknown_user

    showPromptDialog({
      title: t(texts?.remove_user_dialog_title, { userType }),
      message: isOwner ?
        t(texts?.remove_owner_dialog_message, { userTypeLower, userName }) :
        t(texts?.remove_user_dialog_message, { userTypeLower, userName }),
      confirmText: texts?.remove_confirm_text,
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          const response = await RemoveUserFromLicense({
            licenseId: license._id,
            userId: user._id,
          })

          if (response.result) {
            showNotification({
              message: t(texts?.remove_user_success, { userType }),
              className: "bg-success text-white",
            })
            setRefreshKey(prev => prev + 1)
            // Refresh license data
            if (onLicenseUpdate) {
              onLicenseUpdate()
            }
          } else {
            showNotification({
              message: response.message || response.error || t(texts?.remove_user_failed, { userTypeLower }),
              className: "bg-error text-white",
            })
          }
        } catch (error) {
          console.error(`Error removing user:`, error)
          showNotification({
            message: t(texts?.remove_user_error, { userTypeLower }),
            className: "bg-error text-white",
          })
        }
      },
    })
  }

  // Handle bulk user removal
  const handleBulkRemoveUsers = () => {
    if (selectedUsers.length === 0) return

    const userCount = selectedUsers.length
    const userText = userCount === 1 ? texts?.user_singular : texts?.user_plural

    showPromptDialog({
      title: t(texts?.remove_user_count_dialog_title, { userCount, userText }),
      message: t(texts?.remove_user_count_dialog_message, { userCount, userText }),
      confirmText: t(texts?.remove_user_count_confirmation, { userCount, userText }),
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          // Use bulk removal API
          const response = await RemoveUsersFromLicense({
            licenseId: license._id,
            userIds: selectedUsers,
          })

          if (response.result && response.data) {
            const { successCount, errorCount, errors, message } = response.data

            // Show success notification with backend message
            if (successCount > 0) {
              const successText = successCount === 1 ? texts?.user_singular : texts?.user_plural
              showNotification({
                message: message || t(texts?.bulk_user_removal_success, { userCount: successCount, userText: successText }),
                className: "bg-success text-white",
              })
            }

            // Log specific errors for debugging
            if (errors && errors.length > 0) {
              console.error('User removal errors:', errors)
            }

            // Show error notification if all failed
            if (errorCount > 0 && successCount === 0) {
              const errorText = errorCount === 1 ? texts?.user_singular : texts?.user_plural
              showNotification({
                message: t(texts?.bulk_user_removal_fail_all, { userCount: errorCount, userText: errorText }),
                className: "bg-error text-white",
              })
            }

            // Clear selection and refresh data
            setSelectedUsers([])
            setRefreshKey(prev => prev + 1)

            // Refresh license data if any users were removed
            if (successCount > 0 && onLicenseUpdate) {
              onLicenseUpdate()
            }
          } else {
            // Handle API error response
            showNotification({
              message: response.message || response.error || texts?.user_removal_fail,
              className: "bg-error text-white",
            })
          }
        } catch (error) {
          console.error('Bulk user removal error:', error)
          showNotification({
            message: texts?.bulk_user_removal_error,
            className: "bg-error text-white",
          })
        }
      },
    })
  }

  // Handle bulk device removal
  const handleBulkRemoveDevices = () => {
    if (selectedDevices.length === 0) return

    const deviceCount = selectedDevices.length
    const deviceText = deviceCount === 1 ? texts?.device_singular : texts?.device_plural
    const seatText = deviceCount === 1 ? texts?.seat_singular : texts?.seat_plural

    showPromptDialog({
      title: t(texts?.remove_device_count_dialog_title, { deviceCount, deviceText }),
      message: t(texts?.remove_device_count_dialog_message, { deviceCount, deviceText, seatText }),
      confirmText: t(texts?.remove_device_count_confirmation, { deviceCount, deviceText }),
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          // Use bulk device removal API
          const response = await DeactivateDevices({
            licenseId: license._id,
            deviceIds: selectedDevices,
          })

          if (response.result && response.data) {
            const { successCount, errorCount, errors, message } = response.data

            // Show success notification with backend message
            if (successCount > 0) {
              const successText = successCount === 1 ? texts?.device_singular : texts?.device_plural
              showNotification({
                message: message || t(texts?.bulk_device_removal_success, { deviceCount: successCount, deviceText: successText }),
                className: "bg-success text-white",
              })
            }

            // Log specific errors for debugging
            if (errors && errors.length > 0) {
              console.error('Device removal errors:', errors)
            }

            // Show error notification if all failed
            if (errorCount > 0 && successCount === 0) {
              const errorText = errorCount === 1 ? texts?.device_singular : texts?.device_plural
              showNotification({
                message: t(texts?.bulk_device_removal_fail_all, { deviceCount: errorCount, deviceText: errorText }),
                className: "bg-error text-white",
              })
            }

            // Clear selection and refresh data
            setSelectedDevices([])
            setRefreshKey(prev => prev + 1)

            // Refresh license data if any devices were removed
            if (successCount > 0 && onLicenseUpdate) {
              onLicenseUpdate()
            }
          } else {
            // Handle API error response
            showNotification({
              message: response.message || response.error || texts?.device_removal_fail,
              className: "bg-error text-white",
            })
          }
        } catch (error) {
          console.error('Bulk device removal error:', error)
          showNotification({
            message: texts?.bulk_device_removal_error,
            className: "bg-error text-white",
          })
        }
      },
    })
  }

  const deviceTableFooter = selectedDevices.length > 0 && license.isOwner && (
    <div className="flex flex-col sm:flex-row justify-end items-center gap-2 sm:gap-3 mt-4 p-3 bg-paper rounded-3xl">
      <Typography variant="txt_dim">
        {t(texts?.devices_selected_text, {
          deviceCount: selectedDevices.length,
          deviceText: selectedDevices.length === 1 ? texts?.device_singular : texts?.device_plural
        })}
      </Typography>

      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          onClick={() => setSelectedDevices([])}
        >
          {texts?.deselect_btn}
        </Button>

        <Button
          variant="secondary_red"
          onClick={() => handleBulkRemoveDevices()}
        >
          <TrashIcon className="w-4 h-4 mr-2" />
          {texts?.remove_btn}
        </Button>
      </div>
    </div>
  )

  if (!license) return null

  // The interactive seat controls (SeatManagement) replace the static Seats
  // card — showing both duplicates the info.
  const hasSeatControls =
    license.isOwner && license.subscription && license.subscription.status === "active"

  // Status labels — mirrors the status map in Licenses/index.jsx
  const statusLabels = {
    free: texts?.status_free,
    active: texts?.status_active,
    canceled: texts?.status_canceled,
    grace_period: texts?.status_grace,
    on_hold: texts?.status_on_hold,
    paused: texts?.status_paused,
    expired: texts?.status_expired,
    revoked: texts?.status_revoked,
  }
  const statusLabel = statusLabels[license.status?.toLowerCase()] ||
    license.status?.charAt(0).toUpperCase() + license.status?.slice(1) ||
    texts?.status_default

  // Create grid configuration for license information
  const gridConfig = {
    layout: [
      {
        cells: [
          {
            type: "code",
            label: texts?.code_card,
            value: license.code,
            action: {
              icon: <DocumentDuplicateIcon className="w-6 h-6" />,
              onClick: copyLicenseCode,
              tooltip: texts?.code_column_tooltip
            }
          },
          {
            type: "text",
            label: texts?.status_card,
            value: statusLabel,
            valueColor: license.status === 'active' ? 'text-success' :
                       license.status === 'expired' ? 'text-error' :
                       license.status === 'paused' ? 'text-warning' : 'text-primary'
          },
          ...(!hasSeatControls ? [{
            type: "text",
            label: texts?.seats_card,
            value: `${license.seats} ${license.seats === 1 ? texts?.seat_singular : texts?.seat_plural}`
          }] : []),
          {
            type: "text",
            label: texts?.validity_card,
            value: license.validityText,
            valueColor: license.isExpired ? 'text-error' : 'text-text'
          },
          {
            type: "text",
            label: texts?.product_name_card,
            value: license.storeProduct?.name || license.productId || texts?.unknown_product,
            colSpan: 2
          }
        ]
      }
    ]
  }

  const tableFooter = selectedUsers.length > 0 && license.isOwner && (
    <div className="flex flex-col sm:flex-row justify-end items-center gap-2 sm:gap-3 mt-4 p-3 bg-paper rounded-3xl">
      <Typography variant="txt_dim">
        {t(texts?.users_selected_text, {
          userCount: selectedUsers.length,
          userText: selectedUsers.length === 1 ? texts?.user_singular : texts?.user_plural
        })}
      </Typography>

      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          onClick={() => setSelectedUsers([])}
        >
          {texts?.deselect_btn}
        </Button>

        <Button
          variant="secondary_red"
          onClick={() => handleBulkRemoveUsers()}
        >
          <TrashIcon className="w-4 h-4 mr-2" />
          {texts?.remove_btn}
        </Button>
      </div>
    </div>
  )

  return (
    <div className="space-y-6 bg-canvas sm:bg-paper/50 rounded-3xl px-3 py-6 sm:p-6">
      {/* License Management Tabs */}
      <div>
        {/* Horizontal scrollable tabs bar (mobile), with chevrons — same
            pattern as the Account panel navigation. */}
        <div className="relative mb-4">
          {canScrollTabsLeft && (
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 flex items-center justify-start pl-1 bg-gradient-to-r from-canvas to-canvas/90">
              <ChevronLeftIcon className="w-4 h-4 text-text_secondary" />
            </div>
          )}
          {canScrollTabsRight && (
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 flex items-center justify-end pr-1 bg-gradient-to-l from-canvas to-canvas/90">
              <ChevronRightIcon className="w-4 h-4 text-text_secondary" />
            </div>
          )}
          <div ref={tabsScrollRef} className="overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
        <Tabs
          variant="default"
          value={activeTab}
          onChange={handleTabChange}
          tabs={[
            {
              value: "details",
              label: (
                <div className="flex items-center gap-1 md:gap-2">
                  <ClipboardDocumentListIcon className="w-4 lg:w-5 h-4 lg:h-5" />

                  <Typography variant="txt_accent">
                    {texts?.details_tab_label}
                  </Typography>
                </div>
              )
            },
            {
              value: "users",
              label: (
                <div className="flex items-center gap-1 md:gap-2">
                  <UsersIcon className="w-4 lg:w-5 h-4 lg:h-5" />

                  <Typography variant="txt_accent">
                    {texts?.users_tab_label}
                  </Typography>
                </div>
              )
            },
            {
              value: "devices",
              label: (
                <div className="flex items-center gap-1 md:gap-2">
                  <DevicePhoneMobileIcon className="w-4 lg:w-5 h-4 lg:h-5" />

                  <Typography variant="txt_accent">
                    {texts?.devices_tab_label}
                  </Typography>
                </div>
              )
            },
            {
              value: "guide",
              label: (
                <div className="flex items-center gap-1 md:gap-2">
                  <BookOpenIcon className="w-4 lg:w-5 h-4 lg:h-5" />

                  <Typography variant="txt_accent">
                    {texts?.guide_tab_label}
                  </Typography>
                </div>
              )
            },
          ]}
          styleOverrides={{
            tab: "relative px-2 md:px-4 py-2 text-sm font-medium transition-all duration-200 focus:outline-none flex items-center md:gap-2 shrink-0 whitespace-nowrap"
          }}
        />
          </div>
        </div>

        {/* Details Tab */}
        {activeTab === "details" && (
          <div>
            <GridSummary config={gridConfig} variant="snap"/>
            {/* Seat purchase control for per-seat subscription licenses */}
            <SeatManagement license={license} onChanged={onLicenseUpdate} />
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div>
            {/* Seat invites: available keys + pending invites */}
            <InviteUsers
              license={license}
              onGoToDetails={() => handleTabChange("details")}
            />
            <DataTable
              variant="browser"
              apiMethod={GetLicenseUsers}
              requestFields={requestFields}
              refreshKey={refreshKey}
              searchField="name email"
              itemsPerPage={10}
              tableFooter={tableFooter}
              localUserPreferencesKey="account.licenseUsers"
              styleOverrides={{
                container: "relative w-full flex flex-col md:h-[90vh] justify-start bg-paper rounded-3xl px-3 sm:px-6 pt-6 pb-4",
                searchInput: "w-full sm:w-[70%] sm:max-w-[350px] flex-shrink-1 ml-0 sm:mr-4",
                headerRow: "text-left text-text font-normal text-sm border-b border-bordered shadow-sm",
                tableWrapper: "flex-col overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
                table: "w-full min-w-[560px] table-auto",
                getRowStyle: (index, item) => {
                  const isSelected = selectedUsers.includes(item._id)
                  let style = "bg-paper border-b border-bordered"

                  if (isSelected) {
                    style = "bg-neutral border-b border-bordered"
                  }

                  style += " hover:bg-neutral_1/40 transition-colors duration-200 cursor-pointer"
                  return style
                },
                onRowClick: (item) => {
                  const isSelected = selectedUsers.includes(item._id)
                  if (isSelected) {
                    setSelectedUsers((prev) => prev.filter((id) => id !== item._id))
                  } else {
                    setSelectedUsers((prev) => [...prev, item._id])
                  }
                }
              }}
              renders={[
                ...(!isMobile ? [{
                  id: "select",
                  headerClassName: "w-10 min-w-[40px]",
                  header: (_, items) => (
                    <Checkbox
                      isOn={items.length > 0 && selectedUsers.length === items.length}
                      onChange={(isSelected) => {
                        if (isSelected) {
                          setSelectedUsers(items.map((item) => item._id))
                        } else {
                          setSelectedUsers([])
                        }
                      }}
                    />
                  ),
                  render: (_, item) => (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        isOn={selectedUsers.includes(item._id)}
                        onChange={(isSelected) => {
                          if (isSelected) {
                            setSelectedUsers((prev) => [...prev, item._id])
                          } else {
                            setSelectedUsers((prev) => prev.filter((id) => id !== item._id))
                          }
                        }}
                      />
                    </div>
                  ),
                }] : []),
                {
                  id: "user",
                  title: texts?.user_column,
                  headerClassName: "w-[80%] min-w-[220px] text-base md:text-lg",
                  render: (_, user) => {
                    const isPrimaryOwner = license.owner?._id === user._id
                    const isCurrentUser = userProfile?._id === user._id

                    return (
                      <div className="flex items-center space-x-2 md:space-x-4 py-3">
                        <div className={`w-8 md:w-12 h-8 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isPrimaryOwner ? 'bg-gradient-to-br from-accent/70 to-secondary/40' : 'bg-neutral_2'
                        }`}>
                          <Typography
                            variant="body1_accent"
                            className="text-white text-sm"
                          >
                            {(user.name || user.email).charAt(0).toUpperCase()}
                          </Typography>
                        </div>

                        <div>
                          <Typography
                            variant="txt_accent"
                            className="text-text font-medium my-0"
                          >
                            {user.name || user.email}

                            {isCurrentUser && (
                              <span className="text-primary font-normal ml-2">
                                {texts?.you}
                              </span>
                            )}
                          </Typography>

                          {user.name && user.email && (
                            <Typography
                              variant="txt1_dim"
                              className="block truncate max-w-[150px]"
                            >
                              {user.email}
                            </Typography>
                          )}
                        </div>
                      </div>
                    )
                  },
                },
                {
                  id: "role",
                  title: texts?.role_column,
                  headerClassName: "w-[90px] md:w-[150px] min-w-[110px] flex items-center justify-center text-center text-base md:text-lg",
                  render: (_, user) => {
                    const isPrimaryOwner = license.owner?._id === user._id
                    return (
                      <div className="flex items-center justify-center">
                        {isPrimaryOwner ? (
                          <span className="bg-gradient-to-br from-accent/60 to-secondary/50 text-white font-semibold text-xs rounded-full whitespace-nowrap px-2 py-1">
                            {texts?.role_primary_owner}
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-neutral_2 text-white text-xs rounded-full whitespace-nowrap">
                            {texts?.role_secondary_user}
                          </span>
                        )}
                      </div>
                    )
                  },
                },
                ...(license.isOwner ? [{
                  id: "actions",
                  title: "",
                  headerClassName: "w-[60px] min-w-[60px]",
                  render: (_, user) => {
                    const isPrimaryOwner = license.owner?._id === user._id
                    const isCurrentUser = userProfile?._id === user._id

                    // Don't show remove button for current user or if user can't be removed
                    if (isCurrentUser) {
                      return null
                    }

                    return (
                      <div className="flex justify-center">
                        <Button
                          variant="ghost"
                          className="p-1 text-error hover:bg-error hover:text-white"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRemoveUser(user, isPrimaryOwner)
                          }}
                        >
                          <UserMinusIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    )
                  },
                }] : []),
              ]}
              noData={
                <div className="text-center py-4">
                  <UsersIcon className="w-12 h-12 text-text_secondary mx-auto mb-4" />

                  <Typography variant="txt_dim">
                    {texts?.no_data}
                  </Typography>
                </div>
              }
              onDataLoaded={handleUsersDataLoaded}
            />
          </div>
        )}

        {/* Devices Tab */}
        {activeTab === "devices" && (
          <div>
            <DataTable
              variant="browser"
              apiMethod={GetLicenseDevices}
              requestFields={requestFields}
              refreshKey={refreshKey}
              searchField="name model platform"
              showSearch={true}
              itemsPerPage={10}
              tableFooter={deviceTableFooter}
              localUserPreferencesKey="account.licenseDevices"
              headerControls={
                <div className="flex items-center space-x-4">
                  {/* Seat Usage text */}
                  <Typography
                    variant="txt1"
                    className="font-medium text-text whitespace-nowrap"
                  >
                    {t(texts?.seat_usage_text, {
                      used: deviceCount || 0,
                      total: license.seats
                    })}
                  </Typography>

                  {/* Seat Usage Percentage */}
                  <Typography
                    variant="txt1_dim"
                  >
                    {Math.round(((deviceCount || 0) / license.seats) * 100)}%
                  </Typography>
                </div>
              }
              styleOverrides={{
                container: "flex flex-col h-full bg-paper p-4 rounded-3xl",
                header: "w-full flex flex-col sm:flex-row sm:justify-between items-stretch sm:items-end gap-2 my-5",
                headerControls: "w-full flex justify-start sm:justify-end sm:flex-grow",
                searchInput: "w-full sm:w-[70%] sm:max-w-[350px] flex-shrink-1 ml-0 sm:mr-4",
                tableWrapper: "h-auto flex-col overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
                table: "w-full min-w-[480px] table-auto",
                getRowStyle: (index, item) => {
                  const isSelected = selectedDevices.includes(item._id || item.deviceId)
                  let style = "bg-paper border-b border-bordered"

                  if (isSelected) {
                    style = "bg-neutral border-b border-bordered"
                  }

                  style += " hover:bg-neutral_1/40 transition-colors duration-200 cursor-pointer"
                  return style
                },
                onRowClick: (item) => {
                  const deviceId = item._id || item.deviceId
                  const isSelected = selectedDevices.includes(deviceId)
                  if (isSelected) {
                    setSelectedDevices((prev) => prev.filter((id) => id !== deviceId))
                  } else {
                    setSelectedDevices((prev) => [...prev, deviceId])
                  }
                }
              }}
              renders={[
                ...(!isMobile ? [{
                  id: "select",
                  headerClassName: "w-14 min-w-[56px]",
                  header: (_, items) => (
                    <Checkbox
                      isOn={items.length > 0 && selectedDevices.length === items.length}
                      onChange={(isSelected) => {
                        if (isSelected) {
                          setSelectedDevices(items.map((item) => item._id || item.deviceId))
                        } else {
                          setSelectedDevices([])
                        }
                      }}
                    />
                  ),
                  render: (_, item) => (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        isOn={selectedDevices.includes(item._id || item.deviceId)}
                        onChange={(isSelected) => {
                          const deviceId = item._id || item.deviceId
                          if (isSelected) {
                            setSelectedDevices((prev) => [...prev, deviceId])
                          } else {
                            setSelectedDevices((prev) => prev.filter((id) => id !== deviceId))
                          }
                        }}
                      />
                    </div>
                  ),
                }] : []),
                {
                  id: "device",
                  title: texts?.device_column,
                  headerClassName: `${isMobile ? 'w-[60%]' : 'w-1/2'} min-w-[200px]`,
                  render: (_, device) => (
                    <div className="flex items-center space-x-2">
                      {getPlatformIcon(device.platform, "w-8 md:w-10 h-8 md:h-10 text-text_secondary")}

                      <div>
                        <Typography
                          variant="txt"
                          className="font-medium text-text"
                        >
                          {device.name || device.deviceName || texts?.unknown_device}
                        </Typography>

                        {device.model && (
                          <Typography
                            variant="txt2_dim"
                            className="block"
                          >
                            {device.model}
                          </Typography>
                        )}
                      </div>
                    </div>
                  ),
                },
                {
                  id: "owner",
                  title: texts?.owner_column,
                  headerClassName: "min-w-[160px]",
                  render: (_, device) => {
                    // Use the user data provided by the API
                    const owner = device.user

                    if (owner) {
                      const isCurrentUser = userProfile?._id === owner._id
                      const isLicenseOwner = license.owner?._id === owner._id

                      return (
                        <div className="flex items-center space-x-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isLicenseOwner ? 'bg-gradient-to-br from-accent/70 to-secondary/40' : 'bg-neutral_2'
                          }`}>
                            <Typography
                              variant="txt"
                              className="text-white font-medium"
                            >
                              {(owner.name || owner.email).charAt(0).toUpperCase()}
                            </Typography>
                          </div>

                          <div>
                            <Typography
                              variant="txt"
                              className="text-text"
                            >
                              {owner.name || owner.email}

                              {isCurrentUser && (
                                <span className="text-primary ml-1">
                                  {texts?.you}
                                </span>
                              )}
                            </Typography>
                          </div>
                        </div>
                      )
                    }

                    return (
                      <Typography variant="txt_dim">
                        {texts?.unknown_owner}
                      </Typography>
                    )
                  },
                },
              ]}
              noData={
                <div className="text-center py-8">
                  {getPlatformIcon(null, "w-12 h-12 text-text_secondary mx-auto mb-4")}
                  <Typography
                    variant="txt_dim"
                    className="block mb-2"
                  >
                    {texts?.no_devices_activated}
                  </Typography>

                  <Typography variant="txt_dim">
                    {t(texts?.device_activation_info, {
                      seatCount: license.seats,
                      deviceText: license.seats > 1 ? texts?.device_plural : texts?.device_singular
                    })}
                  </Typography>
                </div>
              }
              onDataLoaded={handleDevicesDataLoaded}
            />
          </div>
        )}

        {/* Guide Tab */}
        {activeTab === "guide" && (
          <div className="grid w-full max-w-[700px] mx-auto mb-[50px] grid-cols-1 gap-3">
            <Typography 
              component="h2" 
              variant="h4" 
              className="text-center my-8"
            >
              {texts?.guide_title}
            </Typography>

            <Accordion
              variant="snap_compact"
              items={[
                {
                  question: texts?.guide_understanding_params_q,
                  answer: (
                    <div className="space-y-3">
                      <p>
                        <strong>{texts?.guide_license_code_label}</strong> {texts?.guide_license_code_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_seats_label}</strong> {texts?.guide_seats_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_status_label}</strong> {texts?.guide_status_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_validity_label}</strong> {texts?.guide_validity_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_product_name_label}</strong> {texts?.guide_product_name_desc}
                      </p>
                    </div>
                  )
                },
                {
                  question: texts?.guide_what_you_can_do_q,
                  answer: (
                    <div className="space-y-3">
                      <p>
                        <strong>{texts?.guide_copy_license_label}</strong> {texts?.guide_copy_license_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_manage_users_label}</strong> {texts?.guide_manage_users_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_manage_devices_label}</strong> {texts?.guide_manage_devices_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_bulk_ops_label}</strong> {texts?.guide_bulk_ops_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_search_filter_label}</strong> {texts?.guide_search_filter_desc}
                      </p>
                    </div>
                  )
                },
                ...(license.seats > 1 ? [{
                  question: texts?.guide_multi_seat_q,
                  answer: (
                    <div className="space-y-3">
                      <p>
                        <strong>{texts?.guide_shared_access_label}</strong> {texts?.guide_shared_access_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_seat_mgmt_label}</strong> {texts?.guide_seat_mgmt_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_owner_priv_label}</strong> {texts?.guide_owner_priv_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_user_perms_label}</strong> {texts?.guide_user_perms_desc}
                      </p>
                    </div>
                  )
                }] : []),
                {
                  question: texts?.guide_roles_perms_q,
                  answer: (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-base mb-2">
                          {t(texts?.guide_license_owner_title, {
                            youTag: license.isOwner ? texts?.you : ""
                          })}
                        </h4>
                        <div className="space-y-2 ml-4">
                          <p>{texts?.guide_owner_perm_1}</p>
                          <p>{texts?.guide_owner_perm_2}</p>
                          <p>{texts?.guide_owner_perm_3}</p>
                          <p>{texts?.guide_owner_perm_4}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-base mb-2">
                          {t(texts?.guide_license_user_title, {
                            youTag: !license.isOwner ? texts?.you : ""
                          })}
                        </h4>
                        <div className="space-y-2 ml-4">
                          <p>{texts?.guide_user_perm_1}</p>
                          <p>{texts?.guide_user_perm_2}</p>
                          <p>{texts?.guide_user_perm_3}</p>
                          <p>{texts?.guide_user_perm_4}</p>
                        </div>
                      </div>
                    </div>
                  )
                },
                {
                  question: texts?.guide_security_notes_q,
                  answer: (
                    <div className="space-y-3">
                      <p>
                        <strong>{texts?.guide_keep_secure_label}</strong> {texts?.guide_keep_secure_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_seat_limits_label}</strong> {texts?.guide_seat_limits_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_user_removal_label}</strong> {texts?.guide_user_removal_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_device_mgmt_label}</strong> {texts?.guide_device_mgmt_desc}
                      </p>
                      <p>
                        <strong>{texts?.guide_license_sharing_label}</strong> {texts?.guide_license_sharing_desc}
                      </p>
                    </div>
                  )
                }
              ]}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default LicenseInfo